import { createFilingsAdapter, filingsQuery, FILINGS_TIME_LIST_CONFIG } from './filingsAdapter';
import type { FilingsTimeList } from './filingsAdapter';
import type { LocalDate } from '@js-joda/core';
import { effect } from '@preact/signals-core';
import type { ReadonlySignal } from '@preact/signals-core';
import type { QueryClient } from '@tanstack/query-core';
import { SymbolTrackedTimeList } from '@thinkalpha/ui-symbol-tracked-time-list';
import { createTimeListTransport, getFilingsPageQuery } from 'src/queries/timeList';

export function createFilingsList(client: QueryClient): FilingsTimeList {
    return new SymbolTrackedTimeList({
        source: {
            sourceId: 'filings',
            selectionKind: 'multi-symbol',
            mode: 'live',
            query: filingsQuery(null, [], []),
            retention: 0,
        },
        adapter: createFilingsAdapter(),
        config: FILINGS_TIME_LIST_CONFIG,
        scheduler: {
            now: () => Date.now(),
            schedule(delay, callback) {
                const timer = setTimeout(callback, delay);
                return () => clearTimeout(timer);
            },
        },
        transport: createTimeListTransport(client, getFilingsPageQuery),
    });
}

export class SingleSymbolFilingsModel implements Disposable {
    readonly list: FilingsTimeList;
    readonly #disposables = new DisposableStack();
    constructor(
        client: QueryClient,
        symbol: ReadonlySignal<string>,
        anchor: ReadonlySignal<LocalDate | null>,
        types: ReadonlySignal<readonly string[]>,
        tags: ReadonlySignal<readonly string[]>,
    ) {
        this.list = this.#disposables.use(createFilingsList(client));
        let sourceKey: string | undefined;
        this.#disposables.defer(
            effect(() => {
                const value = symbol.value;
                const query = filingsQuery(anchor.value, types.value, tags.value);
                const key = JSON.stringify([value, query]);
                if (key === sourceKey) return;
                sourceKey = key;
                this.list.activateSource(
                    {
                        sourceId: key,
                        selectionKind: 'single-symbol',
                        mode: query.anchor ? 'historical' : 'live',
                        query,
                        retention: 0,
                    },
                    value ? [value] : [],
                );
            }),
        );
    }
    [Symbol.dispose]() {
        this.#disposables.dispose();
    }
}
